import { Link } from 'react-router-dom';

/**
 * Beautiful empty state — never leave a blank chart or list.
 * Props: icon (emoji or lucide component), title, description, ctaLabel, ctaTo, tone.
 */
export default function EmptyState({
  icon,
  title,
  description,
  ctaLabel,
  ctaTo,
  ctaOnClick,
  tone = 'beige', // beige | white | navy
  testid = 'empty-state',
  compact = false,
}) {
  const bg =
    tone === 'navy'
      ? 'bg-navy text-white'
      : tone === 'white'
      ? 'bg-white card-soft'
      : 'bg-beige';
  const textMuted = tone === 'navy' ? 'text-white/70' : 'text-navy/60';
  // Accept lucide-react icons (forwardRef objects: typeof 'object' with $$typeof/render)
  // as well as plain function components. Fallback to rendering emoji/string.
  const isIconComponent =
    typeof icon === 'function' ||
    (typeof icon === 'object' && icon !== null && (icon.render || icon.$$typeof));
  const Icon = isIconComponent ? icon : null;
  return (
    <div
      data-testid={testid}
      className={`${bg} rounded-3xl ${compact ? 'p-5' : 'p-6 lg:p-10'} flex flex-col items-center text-center gap-3`}
    >
      <div className={`w-14 h-14 lg:w-16 lg:h-16 rounded-2xl ${tone === 'navy' ? 'bg-white/10' : 'bg-white'} flex items-center justify-center text-3xl`}>
        {Icon ? <Icon size={22} className={tone === 'navy' ? 'text-gold' : 'text-navy/70'} strokeWidth={1.5}/> : icon || '✨'}
      </div>
      <p className={`font-heading text-lg lg:text-xl ${tone === 'navy' ? 'text-white' : 'text-navy'} max-w-md leading-snug`}>
        {title}
      </p>
      {description && <p className={`text-sm lg:text-base ${textMuted} max-w-md leading-relaxed`}>{description}</p>}
      {ctaLabel && (ctaTo || ctaOnClick) && (
        ctaTo ? (
          <Link to={ctaTo} data-testid={`${testid}-cta`} className={`mt-2 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-smooth ${
            tone === 'navy' ? 'bg-gold text-white hover:bg-gold-hover' : 'bg-navy text-white hover:bg-navy-soft'
          }`}>
            {ctaLabel}
          </Link>
        ) : (
          <button onClick={ctaOnClick} data-testid={`${testid}-cta`} className={`mt-2 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-smooth ${
            tone === 'navy' ? 'bg-gold text-white hover:bg-gold-hover' : 'bg-navy text-white hover:bg-navy-soft'
          }`}>
            {ctaLabel}
          </button>
        )
      )}
    </div>
  );
}
